// Shared helpers for SQL/NoSQL dialect generators.
// Kept tiny on purpose — each dialect owns its own quoting/identifier rules
// where they diverge (MySQL backticks, T-SQL brackets, etc.).

export const RE_IDENT = /^[A-Za-z_][A-Za-z0-9_]*$/;

export function isSafeIdent(s: string): boolean {
  return RE_IDENT.test(s);
}

/** ANSI-style double-quoted identifier (Postgres / Oracle / Db2 / generic). */
export function qIdent(s: string, quote = true): string {
  if (!quote && isSafeIdent(s)) return s;
  return `"${s.replace(/"/g, '""')}"`;
}

/** MySQL/MariaDB backtick-quoted identifier. */
export function qIdentMysql(s: string): string {
  return `\`${s.replace(/`/g, "``")}\``;
}

/** SQL string literal (single quotes, doubled internally). */
export function qLiteral(s: string): string {
  return `'${s.replace(/'/g, "''")}'`;
}

export type ConnectionString = { label: string; url: string; note?: string };

/** Which UI controls are meaningful for a given engine. */
export interface DialectCapabilities {
  /** Postgres-only DB knobs: encoding, locale, template, owner toggle. */
  pgDatabaseOptions: boolean;
  /** MySQL `user@host` host-pattern field. */
  hostPattern: boolean;
  /** Postgres role attribute checkboxes (LOGIN, CREATEDB, ...). */
  pgRoleAttributes: boolean;
  /** Postgres schema-isolation card (dedicated schema, REVOKE PUBLIC, ...). */
  schemaIsolation: boolean;
  /** Simple "create a dedicated schema" toggle (mssql, db2). */
  schemaToggle: boolean;
  /** Sequence privilege checkboxes (Postgres). */
  sequences: boolean;
  /** USAGE/CREATE on schema + EXECUTE on functions (Postgres). */
  schemaAndFunctions: boolean;
  /** ALTER DEFAULT PRIVILEGES (Postgres). */
  defaultPrivileges: boolean;
  /** TRUNCATE checkbox in table-privileges grid. */
  truncatePriv: boolean;
  /** REFERENCES + TRIGGER checkboxes. */
  referencesAndTrigger: boolean;
  /** "Wrap in BEGIN/COMMIT" toggle. */
  wrapTransaction: boolean;
  /** "Always quote identifiers" toggle. */
  quoteIdentifiers: boolean;
  /** REASSIGN / DROP OWNED hints in update mode (Postgres). */
  pgUpdateHints: boolean;
  /** SUPERUSER (or equivalent) checkbox. */
  superuserToggle: boolean;
  /** Connection-limit numeric input. */
  connectionLimit: boolean;
  /** Valid-until date input. */
  validUntil: boolean;
}

/** Defaults — every cap off. Spread this and only flip the flags you need. */
export const NO_CAPS: DialectCapabilities = {
  pgDatabaseOptions: false,
  hostPattern: false,
  pgRoleAttributes: false,
  schemaIsolation: false,
  schemaToggle: false,
  sequences: false,
  schemaAndFunctions: false,
  defaultPrivileges: false,
  truncatePriv: false,
  referencesAndTrigger: false,
  wrapTransaction: false,
  quoteIdentifiers: false,
  pgUpdateHints: false,
  superuserToggle: false,
  connectionLimit: false,
  validUntil: false,
};
